import { useQuery } from "@tanstack/react-query";
import type { ComparisonMode } from "@/lib/trend-comparison";

export type { ComparisonMode };

export interface TrendSeries {
  key: string;
  label: string;
  data: number[];
}

export interface TrendResult {
  dates: string[];
  series: TrendSeries[];
  currentCount: number;
  previousCount: number | null;
  hasNonSnapshotableConditions: boolean;
}

export type MetricTrendGroupBy =
  | "none"
  | "species"
  | "location"
  | "kennel_status"
  | "adoption_level"
  | "zone";
export type MetricTrendGranularity = "daily" | "weekly";
export type MetricTrendComparison = ComparisonMode;

interface UseMetricTrendOptions {
  days?: number;
  granularity?: MetricTrendGranularity;
  groupBy?: MetricTrendGroupBy;
  comparisonMode?: MetricTrendComparison;
  locationOverride?: string;
}

export function useMetricTrend(
  metricId: string,
  options?: UseMetricTrendOptions,
  enabled = true,
) {
  const days = options?.days ?? 30;
  const granularity = options?.granularity ?? "daily";
  const groupBy = options?.groupBy ?? "none";
  const comparisonMode = options?.comparisonMode ?? "none";
  const locationOverride = options?.locationOverride;

  const params = new URLSearchParams({ days: String(days), granularity, groupBy, comparisonMode });
  if (locationOverride) params.set("locationOverride", locationOverride);

  return useQuery<TrendResult>({
    queryKey: ["/api/metrics", metricId, "trend", { days, granularity, groupBy, comparisonMode, locationOverride }],
    queryFn: async () => {
      const res = await fetch(`/api/metrics/${metricId}/trend?${params}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to fetch metric trend");
      return res.json();
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}
